import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
  ORGANIZATION_ROLES_ACTION_TITLES,
  ORGANIZATION_ROLES_ACTIONS,
  ORGANIZATION_ROLES_MESSAGE,
  ORGANIZATION_ROLES_STATUS,
  OrganizationRoleCredentialPresentation,
  OrganizationRoleRow,
} from 'features/organization-settings/organization-roles/organization-roles.constants';
import {
  Action,
  MessageQueryService,
  PageAction,
  skipModalClose,
  SmartModalService,
  SmartTable,
} from '@certemy/ui-components';
import { PolicyPermissionsService } from 'services/policy-permission/policy-permissions.service';
import { ComputedFrom } from '@certemy/common';
import { catchError, filter, map, switchMap, take } from 'rxjs/operators';
import { OrganizationRolesModalComponent } from 'features/organization-settings/organization-roles/organization-roles-modal/organization-roles-modal';
import _get from 'lodash-es/get';
import { ActivatedRoute } from '@angular/router';
import { OrganizationRolesService } from 'features/organization-settings/organization-roles/organization-roles.service';
import { NEVER, Observable } from 'rxjs';
import _size from 'lodash-es/size';
import { ActiveCredentialsTableModalComponent } from 'features/organization-settings/organization-roles/active-credentials-table-modal/active-credentials-table-modal';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
  selector: 'cm-organization-roles',
  templateUrl: './organization-roles.component.html',
  styleUrls: ['./organization-roles.component.scss'],
})
export class OrganizationRolesComponent implements OnInit, OnDestroy {
  @ViewChild('actionTemplate', { static: true }) actionTemplate;
  @ViewChild('noActiveCredentialsWarningTemplate', { static: true }) noActiveCredentialsWarningTemplate;

  readonly ORGANIZATION_ROLES_ACTIONS = ORGANIZATION_ROLES_ACTIONS;
  readonly tableConfig: SmartTable.Config = {
    rowClickable: false,
  };
  readonly noContentConfig = {
    title: 'No organization roles exist.',
    subtitle: 'Please select the "Add Role" above to add.',
  };
  editPermission = false;

  rows: OrganizationRoleRow[];

  actions: PageAction[] = [
    {
      type: ORGANIZATION_ROLES_ACTIONS.ADD_ROLE,
      name: ORGANIZATION_ROLES_ACTION_TITLES[ORGANIZATION_ROLES_ACTIONS.ADD_ROLE],
    },
  ];

  @ComputedFrom('actionTemplate', 'editPermission')
  get tableHeaders(): SmartTable.Header[] {
    return [
      { name: 'Role Name', value: 'name', type: SmartTable.ColumnTypeId.STRING },
      ...(this.editPermission
        ? [
            {
              name: 'Actions',
              width: 100,
              maxWidth: 100,
              value: this.actionTemplate,
              type: SmartTable.ColumnTypeId.TEMPLATE,
            },
          ]
        : []),
    ];
  }

  constructor(
    private policyPermissionsService: PolicyPermissionsService,
    private smartModalService: SmartModalService,
    private activateRoute: ActivatedRoute,
    private organizationRolesService: OrganizationRolesService,
    private messageService: MessageQueryService,
  ) {}

  ngOnInit() {
    const routerRolesData$ = this.activateRoute.data.pipe(map((data) => data.roles));
    this.editPermission = this.activateRoute.snapshot.data.editPermission;
    this.setRowsData(routerRolesData$);
  }

  ngOnDestroy() {}

  onPageAction(eventTypeId: ORGANIZATION_ROLES_ACTIONS | Action, row?: OrganizationRoleRow, event?) {
    if (event) {
      event.stopPropagation();
    }
    switch (eventTypeId) {
      case ORGANIZATION_ROLES_ACTIONS.ADD_ROLE:
        this.openModal();
        break;
      case ORGANIZATION_ROLES_ACTIONS.DELETE_ROLE:
        this.openDeleteConfirmModal()
          .pipe(
            switchMap(() =>
              this.organizationRolesService.updateOrganizationRole(row.organizationRoleId, {
                ...row,
                statusId: ORGANIZATION_ROLES_STATUS.INACTIVE,
              }),
            ),
            catchError((err) => {
              const text = _get(err, 'body.error_message');
              if (text.toLowerCase().includes('role is assigned')) {
                this.smartModalService
                  .openNotification({
                    title: 'WARNING',
                    body: ORGANIZATION_ROLES_MESSAGE.ROLE_IS_ASSIGNED,
                    closeButtonText: 'Close',
                  })
                  .pipe(take(1))
                  .subscribe();
                return NEVER;
              }
            }),
          )
          .subscribe(() => {
            this.messageService.addMessage('Organization Role was successfully deleted.');
            this.updateTableData();
          });
        break;
      case ORGANIZATION_ROLES_ACTIONS.EDIT_ROLE:
        this.openModal(row);
        break;
      case ORGANIZATION_ROLES_ACTIONS.VIEW_CREDENTIALS:
        this.viewCredentialsModal(row);
        break;
    }
  }

  private openModal(row?) {
    return this.smartModalService
      .open(OrganizationRolesModalComponent, { row }, { windowClass: 'lg' })
      .pipe(take(1), untilDestroyed(this), skipModalClose())
      .subscribe(() => {
        this.updateTableData();
        this.messageService.addMessage(`Organization Role was successfully ${row ? 'updated' : 'added'}.`);
      });
  }

  private viewCredentialsModal(row) {
    this.organizationRolesService
      .getActiveCredentials(row.organizationRoleId)
      .pipe(
        switchMap((rows: OrganizationRoleCredentialPresentation[]) => {
          if (!_size(rows)) {
            return this.smartModalService.openNotification({
              title: 'WARNING',
              body: this.noActiveCredentialsWarningTemplate,
            });
          }
          return this.smartModalService
            .open(ActiveCredentialsTableModalComponent, { rows }, { windowClass: 'x-lg' })
            .pipe(skipModalClose());
        }),
        take(1),
      )
      .subscribe();
  }

  private updateTableData() {
    return this.setRowsData(this.organizationRolesService.getOrganizationRoles());
  }

  private setRowsData(obs$: Observable<OrganizationRoleRow[]>) {
    obs$.pipe(take(1)).subscribe((data) => (this.rows = data));
  }

  private openDeleteConfirmModal(): Observable<any> {
    return this.smartModalService
      .openConfirm({
        title: 'Confirm your action',
        body: 'Are you sure you would like to delete this role?',
        successButtonText: 'Confirm',
      })
      .pipe(
        take(1),
        filter((confirm: boolean) => confirm),
      );
  }
}
